import { useState } from 'react';
import { Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  MAX_BYPASS_PHONES,
  normalizeBypassPhone,
} from '@/lib/bot-service-hours';

// Bypass numbers: the working-hours gate ignores messages from these senders entirely —
// no lock, no auto-reply, receipts as normal — matched by digit SUFFIX server-side, so
// one entry covers the number with or without its country code. Part of the shared
// draft: additions/removals land with the page's Save, like every non-emergency field.
export function BypassPhonesEditor({ phones, disabled, error, onChange }) {
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState('');

  const list = Array.isArray(phones) ? phones : [];

  const add = () => {
    if (disabled) return;
    const raw = input.trim();
    if (!raw) return;
    const { digits, error: parseError } = normalizeBypassPhone(raw);
    if (parseError) {
      setInputError(parseError);
      return;
    }
    if (list.includes(digits)) {
      setInputError('That number is already listed.');
      return;
    }
    if (list.length >= MAX_BYPASS_PHONES) {
      setInputError(`At most ${MAX_BYPASS_PHONES} numbers.`);
      return;
    }
    onChange([...list, digits]);
    setInput('');
    setInputError('');
  };

  const remove = (digits) => {
    if (disabled) return;
    onChange(list.filter((phone) => phone !== digits));
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card px-3 py-2.5">
      {list.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">
          No bypass numbers — every sender follows the working hours.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {list.map((digits) => (
            <span
              key={digits}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 py-0.5 pl-2 pr-1 font-mono text-[12px] text-foreground"
            >
              {digits}
              {!disabled ? (
                <button
                  type="button"
                  className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={`Remove ${digits}`}
                  onClick={() => remove(digits)}
                >
                  <X className="size-3" aria-hidden />
                </button>
              ) : null}
            </span>
          ))}
        </div>
      )}
      {!disabled ? (
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <Input
              value={input}
              placeholder="e.g. +65 6039 13823"
              className="h-8 font-mono text-[13px]"
              onChange={(event) => {
                setInput(event.target.value);
                if (inputError) setInputError('');
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  add();
                }
              }}
            />
            {inputError ? (
              <p className="mt-1 text-[11px] text-destructive">{inputError}</p>
            ) : null}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1"
            disabled={!input.trim()}
            onClick={add}
          >
            <Plus className="size-3.5" aria-hidden />
            Add
          </Button>
        </div>
      ) : null}
      {error ? <p className="text-[11px] text-destructive">{error}</p> : null}
    </div>
  );
}
